/**
 * Seasonal Features Configuration
 *
 * Toggle the weather machine hero and resolve the active season theme
 * (snow / fireworks / motes / leaves) from the calendar — or force one
 * with SEASON_OVERRIDE while developing.
 */

export const SEASONAL_FEATURES = {
  // true = weather machine in hero instead of motto particle canvas
  SNOW_MACHINE_ENABLED: true,

  // null = auto by month; or 'winter' | 'spring' | 'summer' | 'fall'
  SEASON_OVERRIDE: null,
};

/** @typedef {'winter' | 'spring' | 'summer' | 'fall'} SeasonId */

export const SEASON_THEMES = {
  winter: {
    id: 'winter',
    behavior: 'snow',
    machineName: 'weather machine',
    attribution: 'weather machine · winter · by Myke D.',
    spawnLabel: 'Snow Falls Per Minute',
    actionLabel: 'Snow Fall',
    stopLabel: 'Stop Snow',
    pieceLabel: 'Pieces',
    skyColor: '#1a1a2e',
    colors: ['#ffffff', '#f0f8ff', '#e6f3ff', '#f5f5f5', '#e8f4f8', '#f0f0f0', '#e6e6fa', '#f8f8ff'],
    combineEnabled: true,
    settleEnabled: true,
    fadeOut: false,
    tumble: false,
    spawnRateMin: 100,
    spawnRateMax: 6000,
    spawnRateStep: 50,
    defaultSpawnRate: null, // randomize in sim as today
  },
  spring: {
    id: 'spring',
    behavior: 'motes',
    machineName: 'weather machine',
    attribution: 'weather machine · spring · by Myke D.',
    spawnLabel: 'Motes Per Minute',
    actionLabel: 'Mote Fall',
    stopLabel: 'Stop Motes',
    pieceLabel: 'Motes',
    skyColor: '#1e2438',
    colors: ['#e8f5e9', '#c8e6c9', '#a5d6a7', '#ffeef5', '#f3e5f5', '#e1f5fe', '#fff9c4', '#dcedc8'],
    combineEnabled: false,
    settleEnabled: false,
    fadeOut: true,
    tumble: false,
    spawnRateMin: 100,
    spawnRateMax: 4000,
    spawnRateStep: 50,
    defaultSpawnRate: null,
  },
  summer: {
    id: 'summer',
    behavior: 'fireworks',
    machineName: 'weather machine',
    attribution: 'weather machine · summer · by Myke D.',
    spawnLabel: 'Volleys Per Minute',
    actionLabel: 'Fireworks',
    stopLabel: 'Stop Fireworks',
    pieceLabel: 'Sparks',
    skyColor: '#0a0a18',
    colors: ['#ff5252', '#ff9800', '#ffeb3b', '#69f0ae', '#40c4ff', '#e040fb', '#ff4081', '#ffffff'],
    combineEnabled: false,
    settleEnabled: false,
    fadeOut: true,
    tumble: false,
    spawnRateMin: 2,
    spawnRateMax: 90,
    spawnRateStep: 1,
    defaultSpawnRate: 18,
  },
  fall: {
    id: 'fall',
    behavior: 'leaves',
    machineName: 'weather machine',
    attribution: 'weather machine · fall · by Myke D.',
    spawnLabel: 'Leaves Per Minute',
    actionLabel: 'Leaf Fall',
    stopLabel: 'Stop Leaves',
    pieceLabel: 'Leaves',
    skyColor: '#1a1824',
    colors: ['#e65100', '#ef6c00', '#ff8f00', '#f9a825', '#bf360c', '#8d6e63', '#d84315', '#ffcc80'],
    combineEnabled: false,
    settleEnabled: true,
    fadeOut: true,
    tumble: true,
    spawnRateMin: 100,
    spawnRateMax: 4000,
    spawnRateStep: 50,
    defaultSpawnRate: null,
  },
};

/**
 * @param {Date} [date]
 * @returns {SeasonId}
 */
export function getActiveSeason(date = new Date()) {
  const override = SEASONAL_FEATURES.SEASON_OVERRIDE;
  if (override && SEASON_THEMES[override]) {
    return override;
  }

  const month = date.getMonth(); // 0 = Jan
  if (month >= 10 || month <= 1) return 'winter'; // Nov–Feb
  if (month >= 2 && month <= 4) return 'spring'; // Mar–May
  // Fall leaves are unfinished (pines + sky-spawn). Keep fireworks through Oct.
  return 'summer'; // Jun–Oct
}

/**
 * @param {Date} [date]
 * @returns {typeof SEASON_THEMES[SeasonId]}
 */
export function getActiveTheme(date = new Date()) {
  return SEASON_THEMES[getActiveSeason(date)];
}
